import escapeRegexString from 'escape-string-regexp';

/**
 * A function which performs no action, named noop to facilitate code search and
 * usage in certain expressions.
 */
export function noop() {}
/**
 * Format status string
 * @param {String} status
 * @return {String}
 */
export function displayAdStatus(status) {
  return status
    .replace(/_/g, ' ')
    .replace(/\w\S*/g, s => s.charAt(0).toUpperCase() + s.substr(1).toLowerCase());
}

const DEFAULT_GENERATOR = () => Date.now();

/**
 * Append a parameter to the given url for the purposes of avoiding the local resource cache.
 * @param {String} url The URL to modify
 * @param {String} parameter The name of the cachebusting parameter [default = 'cachebuster']
 * @param {Function} generator A function which will generate a cachebusting value
 */
export function appendCachebuster(url, parameter = 'cachebuster', generator = DEFAULT_GENERATOR) {
  const urlWithSep = url + (url.indexOf('?') === -1 ? '?' : '');
  return urlWithSep + '&' + parameter + '=' + generator();
}

/**
 * Converts an array of Objects to an Object keyed by given fieldName
 * @param {Array} arr
 * @param {String} fieldName
 * @return {Object}
 */
export function arrayToObjectByField(arr, fieldName) {
  return arr.reduce((obj, i) => {
    const field = i[fieldName];
    if (!obj[field]) {
      obj[field] = [];
    }
    obj[field].push(i);
    return obj;
  }, {});
}

// http://codereview.stackexchange.com/questions/23899/faster-javascript-fuzzy-string-matching-function
export function fuzzyMatch(str, pattern) {
  if (!pattern) return true;

  const ptn = pattern.split('').reduce((a, b) => {
    const escaped = escapeRegexString(b);
    return a + '[^' + escaped + ']*' + escaped;
  });

  return (new RegExp(ptn.toLowerCase())).test(str.toLowerCase());
}

/**
 * Populate an object with given keys and initial value
 * @param {Iterator} keys
 * @param {*} initialValue
 * @return {Object}
 */
export function fillObject(keys, initialValue) {
  return Array.from(keys).reduce((obj, k) => {
    obj[k] = initialValue;
    return obj;
  }, {});
}

/**
 * @param {number|string} ageMax
 * @return {number|string}
 */
export function ageMaxConverter(ageMax) {
  if (parseInt(ageMax, 10) >= 65) {
    return '65+';
  }
  return ageMax;
}

/**
 * Traverse an object of any depth using a string path
 * i.e. Assign `true` to 'obj.path.to.key' in `obj`
 *
 * @param {string|array} deepKey - Array of keys in order of depth
 *                                 Or '.' separated key path string
 * @param {any} value - Will brute-force override existing value at key path
 * @param {obj|any} state - Existing state at this depth
 * @return {obj} - new state object
 */
export function unflattenAssign(deepKey, value, state = {}) {
  const deepKeys = deepKey.split ? deepKey.split('.') : deepKey;
  const key = deepKeys.shift();
  if (typeof key === 'undefined') {
    return value;
  }
  const deeperState = state.hasOwnProperty(key) ? state[key] : {};

  return {
    ...state,
    [key]: unflattenAssign(deepKeys, value, deeperState),
  };
}

/**
 * Convert string to title case format
 * @param {string} str - String that will be coverted to title case
 * @return {string} - returns the string in title case format
 */
export function toTitleCase(str) {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

export function sortByKeyAsc(key, arr) {
  return arr.sort((a, b) => {
    const aLowercase = a[key].toLowerCase();
    const bLowercase = b[key].toLowerCase();

    if (aLowercase > bLowercase) return 1;
    if (aLowercase < bLowercase) return -1;
    return 0;
  });
}

export function ballparkNumber(num) {
  const roundedNum = Math.round(num);
  const length = roundedNum.toString().length;
  const divisor = Math.pow(10, length - (length > 3 ? 2 : 1));
  return Math.round(roundedNum / divisor) * divisor;
}

export function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}

export function inRange(num, min, max) {
  const parsedNum = parseInt(num, 10);
  return !isNaN(parsedNum) && num >= min && num <= max;
}

export function isHostLocal(hostname) {
  return (
    hostname === 'localhost' ||
    hostname === 'adstudio-local.spotify.com'
  );
}



// WEBPACK FOOTER //
// ./src/utilities.js